<script lang="ts">
	import SvelteSeo from 'svelte-seo';
	import { createPageSEO, createWebPageSchema } from '$lib/config/seo';
	import { Button } from '$lib/components/ui/button';
	import { PUBLIC_EMAIL_PRIVACY } from '$env/static/public';
	import { page } from '$app/stores';
	const seo = createPageSEO({
		title: 'Privacy Policy - TalentProfile',
		description: 'Learn how TalentProfile collects, uses, and protects your data. GDPR compliant.',
		canonical: '/privacy',
		structuredData: createWebPageSchema({
			name: 'Privacy Policy',
			description: 'TalentProfile privacy policy and data handling practices',
			url: '/privacy'
		})
	});
</script>

<SvelteSeo {...seo} />

<div class="min-h-[calc(100dvh-527px)] bg-gradient-to-b from-white to-gray-50">
	<!-- Content -->
	<div class="container mx-auto px-4 py-20">
		<div class="mx-auto max-w-4xl">
			<!-- Hero -->
			<div class="mb-16 text-center">
				<h1 class="mb-4 text-4xl font-bold text-gray-900 md:text-5xl">Privacy Policy</h1>
				<p class="text-lg text-gray-600">Last updated: November 2025</p>
			</div>

			<!-- Content -->
			<div class="prose prose-lg max-w-none">
				<div class="mb-8 rounded-lg border bg-white p-8">
					<h2 class="mb-4 text-2xl font-bold text-gray-900">Our Commitment to Privacy</h2>
					<p class="leading-relaxed text-gray-600">
						At Talent<strong>Profile</strong>, we take your privacy seriously. This Privacy Policy
						explains how we collect, use, disclose, and safeguard your information when you receive
						job alerts from our service.
					</p>
				</div>

				<div class="space-y-8">
					<section class="rounded-lg border bg-white p-8">
						<h2 class="mb-4 text-2xl font-bold text-gray-900">1. How We Collect Information</h2>

						<h3 class="mt-6 mb-3 text-xl font-semibold text-gray-900">Public Data Collection</h3>
						<p class="mb-4 leading-relaxed text-gray-600">
							Talent<strong>Profile</strong> collects publicly available professional information from
							sources including:
						</p>
						<ul class="space-y-2 text-gray-600">
							<li>• GitHub profiles and repositories</li>
							<li>• LinkedIn public profiles</li>
							<li>• Other publicly accessible developer platforms</li>
						</ul>
						<p class="mt-4 leading-relaxed text-gray-600">
							<strong>Important:</strong> We only collect information that you have made publicly available.
							If you've published your profile on GitHub or LinkedIn, that data is already accessible
							to anyone on the internet.
						</p>

						<h3 class="mt-6 mb-3 text-xl font-semibold text-gray-900">Information We Collect</h3>
						<p class="mb-4 leading-relaxed text-gray-600">From public sources, we collect:</p>
						<ul class="space-y-2 text-gray-600">
							<li>• Username and public name</li>
							<li>• Email address (if publicly listed)</li>
							<li>• Technical skills and programming languages</li>
							<li>• Work experience and projects</li>
							<li>• Location (if specified)</li>
							<li>• Bio and professional description</li>
						</ul>

						<h3 class="mt-6 mb-3 text-xl font-semibold text-gray-900">Usage Information</h3>
						<p class="mb-4 leading-relaxed text-gray-600">
							When you interact with our emails, we collect:
						</p>
						<ul class="space-y-2 text-gray-600">
							<li>• Email opens and link clicks</li>
							<li>• Job preferences (when you update them)</li>
							<li>• Email frequency preferences</li>
						</ul>
					</section>

					<section class="rounded-lg border bg-white p-8">
						<h2 class="mb-4 text-2xl font-bold text-gray-900">2. How We Use Your Information</h2>
						<p class="mb-4 leading-relaxed text-gray-600">We use your information to:</p>
						<ul class="space-y-2 text-gray-600">
							<li>• Match you with relevant job opportunities based on your skills</li>
							<li>• Send personalized job alerts to your email</li>
							<li>• Improve our matching algorithm</li>
							<li>• Analyze which types of jobs are most relevant to developers</li>
							<li>• Prevent fraud and ensure service quality</li>
						</ul>
					</section>

					<section class="rounded-lg border bg-white p-8">
						<h2 class="mb-4 text-2xl font-bold text-gray-900">3. No Sign-Up Required</h2>
						<p class="leading-relaxed text-gray-600">
							<strong>You did not sign up for TalentProfile.</strong> We discovered your
							publicly available profile and identified you as a potential match for tech job opportunities.
							If you have an email address associated with your public profile, you may receive job alerts
							from us.
						</p>
						<p class="mt-4 leading-relaxed text-gray-600">
							You can manage your preferences or delete your data at any time using the preference
							link in any email we send you.
						</p>
					</section>

					<section class="rounded-lg border bg-white p-8">
						<h2 class="mb-4 text-2xl font-bold text-gray-900">4. Information Sharing</h2>
						<p class="mb-4 leading-relaxed text-gray-600">
							<strong>We do not sell your personal information.</strong> We may share your information
							only in these circumstances:
						</p>
						<ul class="space-y-2 text-gray-600">
							<li>
								• <strong>Service providers:</strong> With vendors who help operate our platform (email
								delivery, analytics)
							</li>
							<li>
								• <strong>Legal requirements:</strong> When required by law or to protect our rights
							</li>
							<li>
								• <strong>Business transfers:</strong> In connection with a merger, acquisition, or sale
								of assets
							</li>
						</ul>
						<p class="mt-4 leading-relaxed text-gray-600">
							<strong>We never share your data with job posters or employers</strong> unless you apply
							to a job directly through their website.
						</p>
					</section>

					<section class="rounded-lg border bg-white p-8">
						<h2 class="mb-4 text-2xl font-bold text-gray-900">5. Data Security</h2>
						<p class="leading-relaxed text-gray-600">
							We implement industry-standard security measures to protect your information:
						</p>
						<ul class="mt-4 space-y-2 text-gray-600">
							<li>• Encrypted data transmission (HTTPS/TLS)</li>
							<li>• Secure database storage</li>
							<li>• Access controls and authentication</li>
							<li>• Regular security audits</li>
						</ul>
					</section>

					<section class="rounded-lg border bg-white p-8">
						<h2 class="mb-4 text-2xl font-bold text-gray-900">6. Your Privacy Rights</h2>
						<p class="mb-4 leading-relaxed text-gray-600">You have the right to:</p>
						<ul class="space-y-2 text-gray-600">
							<li>
								• <strong>Access:</strong> View what data we have about you (contact us at {PUBLIC_EMAIL_PRIVACY})
							</li>
							<li>• <strong>Correction:</strong> Update your email preferences</li>
							<li>
								• <strong>Deletion:</strong> Completely remove all your data from our system (via preferences
								page)
							</li>
							<li>• <strong>Opt-out:</strong> Unsubscribe from email communications at any time</li>
							<li>
								• <strong>Object:</strong> Stop us from processing your public data for job matching
							</li>
						</ul>
						<p class="mt-4 leading-relaxed font-semibold text-gray-600">
							To exercise any of these rights, use the preference link in any email from us, or
							contact {PUBLIC_EMAIL_PRIVACY}
						</p>
					</section>

					<section class="rounded-lg border bg-white p-8">
						<h2 class="mb-4 text-2xl font-bold text-gray-900">7. Deleting Your Data</h2>
						<p class="mb-4 leading-relaxed text-gray-600">
							You can delete all your data from Talent<strong>Profile</strong> at any time:
						</p>
						<ol class="list-inside list-decimal space-y-2 text-gray-600">
							<li>Click the preference link in any email from us</li>
							<li>Scroll down and click "Delete My Data"</li>
							<li>Confirm the deletion</li>
						</ol>
						<p class="mt-4 leading-relaxed text-gray-600">
							<strong>What happens when you delete:</strong>
						</p>
						<ul class="mt-2 space-y-2 text-gray-600">
							<li>• All your personal information is permanently removed</li>
							<li>• You will never receive emails from us again</li>
							<li>• We retain anonymized analytics data (no personal identifiers)</li>
							<li>• The deletion is immediate and cannot be undone</li>
						</ul>
					</section>

					<section class="rounded-lg border bg-white p-8">
						<h2 class="mb-4 text-2xl font-bold text-gray-900">
							8. Legal Basis for Processing (GDPR)
						</h2>
						<p class="mb-4 leading-relaxed text-gray-600">
							For users in the EU/EEA, our legal basis for processing your data is:
						</p>
						<ul class="space-y-2 text-gray-600">
							<li>
								• <strong>Legitimate Interest:</strong> Matching qualified candidates with job opportunities
							</li>
							<li>• <strong>Public Data:</strong> Information you've made publicly available</li>
						</ul>
						<p class="mt-4 leading-relaxed text-gray-600">
							You have the right to object to this processing at any time by deleting your data
							through the preferences page.
						</p>
					</section>

					<section class="rounded-lg border bg-white p-8">
						<h2 class="mb-4 text-2xl font-bold text-gray-900">9. Children's Privacy</h2>
						<p class="leading-relaxed text-gray-600">
							Talent<strong>Profile</strong> is not intended for users under 16. We do not knowingly collect
							or process data from children under 16.
						</p>
					</section>

					<section class="rounded-lg border bg-white p-8">
						<h2 class="mb-4 text-2xl font-bold text-gray-900">10. Changes to This Policy</h2>
						<p class="leading-relaxed text-gray-600">
							We may update this Privacy Policy from time to time. We will notify you of material
							changes via email. Continued receipt of our emails after changes constitutes
							acceptance of the updated policy.
						</p>
					</section>

					<section class="rounded-lg border bg-white p-8">
						<h2 class="mb-4 text-2xl font-bold text-gray-900">11. Contact Us</h2>
						<p class="mb-4 leading-relaxed text-gray-600">
							If you have questions about this Privacy Policy or want to exercise your rights:
						</p>
						<div class="space-y-2 text-gray-600">
							<p><strong>Email:</strong> {PUBLIC_EMAIL_PRIVACY}</p>
							<p>
								<strong>Support:</strong>
								<a href="/contact" class="text-blue-600 hover:underline">Contact Form</a>
							</p>
							<p><strong>Data Deletion:</strong> Use the preference link in any email from us</p>
						</div>
					</section>
				</div>
			</div>

			<!-- CTA -->
			<div
				class="mt-16 rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 p-12 text-center text-white"
			>
				<h2 class="mb-4 text-2xl font-bold md:text-3xl">Have Questions?</h2>
				<p class="mb-6 opacity-90">We're here to help. Reach out to our team anytime.</p>
				<Button size="lg" variant="secondary" onclick={() => (window.location.href = '/contact')}>
					Contact Us
				</Button>
			</div>
		</div>
	</div>
</div>
